/**
 * @file TagInput.js
 * @description A component for adding and removing tags on a text input field.
 * It manages internal state for tags and communicates changes to the parent component via a callback.
 *
 * @prop {function} onTagsChange - Callback function that returns current tag array ["tag1", "tag2", ...] when they change. Example: (tags) => console.log(tags)
 * @prop {array} savedTags - Initial array of tags to pre-populate the input. Example: ["tag1", "tag2", ...]
 *
 * @returns Renders a list of tags and an input field for adding new tags.
 *
 * @example
 * const ParentComponent = () => {
 *   const [tags, setTags] = useState(["tag1", "tag2"]);
 *   const tagInputRef = useRef(null);
 *   const handleTagsChange = (newTags) => setTags(newTags);
 *   return <TagInput ref={tagInputRef} onTagsChange={handleTagsChange} savedTags={tags} />;
 * };
 **/

import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import './TagInput.css';
import { getText } from '../../i18n';

const TagInput = forwardRef(({ onTagsChange, savedTags, disabled = false }, ref) => {
  const [tags, setTags] = useState(Array.isArray(savedTags) ? savedTags : []);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);
  const prevTagsRef = useRef(tags);

  /**
   * Exposes a flush() method via ref so parent components (e.g. AssetDrawer)
   * can commit any pending typed-but-not-yet-confirmed input before saving.
   *
   * IMPORTANT: flush() returns the complete final tags array synchronously.
   * The caller MUST use this return value directly rather than reading from
   * asset state, because React setState is async — by the time handleSaveAsset
   * reads asset.assetTags, the onTagsChange -> onChangeAsset -> setAsset chain
   * may not have completed yet.
   *
   * Usage in AssetDrawer:
   *   const flushedTags = tagInputRef.current?.flush();
   *   formattedAsset.assetTags = flushedTags ?? asset.assetTags;
   */
  useImperativeHandle(ref, () => ({
    // Returns the pending typed string (if any) and clears the input.
    // Does NOT return or touch the full tags array — the caller is
    // responsible for appending this to whatever tags are already in state.
    flushPending() {
      const trimmed = inputValue.trim();
      if (trimmed) {
        setTags(prevTags => [...prevTags, trimmed]);
        setInputValue('');
        return trimmed;
      }
      return null;
    },
  }));

  useEffect(() => {
    if (onTagsChange && prevTagsRef.current !== tags) {
      onTagsChange(tags);
      prevTagsRef.current = tags;
      console.log('Tags updated:', tags);
    }
  }, [tags, onTagsChange]);

  useEffect(() => {
    if (JSON.stringify(savedTags) !== JSON.stringify(tags)) {
      setTags(Array.isArray(savedTags) ? savedTags : []);
    }
  }, [savedTags]);

  const handleKeyDown = (e) => {
    if (e.key === ' ' || e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        setTags(prevTags => [...prevTags, inputValue.trim()]);
        setInputValue('');
        setTimeout(() => {
          inputRef.current.focus();
        }, 0);
      }
    }
  };

  const removeTag = (index) => {
    setTags(prevTags => prevTags.filter((_, i) => i !== index));
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleFocus = () => {
    const range = document.createRange();
    const selection = window.getSelection();
    range.selectNodeContents(inputRef.current);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  return (
    <div className="tag-input" style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <div className="tags">
        {tags.map((tag, index) => (
          <div key={index} className="tag">
            {!disabled && <i className="remove-tag bi bi-x" onClick={() => removeTag(index)}></i>}
            {tag}
          </div>
        ))}
        <input
          ref={inputRef}
          className="input-area"
          value={inputValue}
          onKeyDown={handleKeyDown}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={getText('msg_a8d375856c58')}
          disabled={disabled}
        />
      </div>
    </div>
  );
});

export default TagInput;
